import { Stack } from "@fluentui/react";
import React, { useEffect } from "react";
import { validateProps } from "../../../../shared/props";
import { ComponentValidator, validateComponent } from "../../../../shared/validation";
import { SlotStack, SlotStackItem } from "../../../Stacks/SlotStack/SlotStack";
import { StackProps } from "../../../Stacks/Stack/Stack";
import { ListItemCanvas, ListItemCanvasExternalProps } from "../shared/ListItemCanvas";
import { validColumnComponents } from "../shared/MultiColumnListItem.types";
import { listItemPadding, multiColumnTotalSlots } from "../shared/styles";
import { validateListItemColumnComponent } from "../shared/utils";

export type ListItemVariant =
    "ThreeThreeThreeThree" |
    "SixTwoTwoTwo" |
    "SixThreeThree" |
    "ThreeThreeSix" |
    "SixTwoFour" |
    "SixFourTwo" |
    "ThreeSixThree" |
    "SixSix" |
    "EightFour" |
    "Twelve" |
    "EightTwoTwo" |
    "FourFourFour" |
    "FourEight" |
    "TwoTwoTwoTwoTwo" |
    "ThreeNine";

// tslint:disable: no-magic-numbers
const variantColumnWidths: { [key in ListItemVariant]: readonly number[] } = {
    "ThreeThreeThreeThree": [3, 3, 3, 3],
    "SixTwoTwoTwo": [6, 2, 2, 2],
    "SixThreeThree": [6, 3, 3],
    "ThreeThreeSix": [3, 3, 6],
    "SixTwoFour": [6, 2, 4],
    "SixFourTwo": [6, 4, 2],
    "ThreeSixThree": [3, 6, 3],
    "EightTwoTwo": [8, 2, 2],
    "FourFourFour": [4, 4, 4],
    "SixSix": [6, 6],
    "EightFour": [8, 4],
    "FourEight": [4, 8],
    "Twelve": [12],
    "TwoTwoTwoTwoTwo": [2.4, 2.4, 2.4, 2.4, 2.4],
    "ThreeNine": [3, 9]
};

const buildListItemLengthValidator = (columnsLength: number, columnWidthsLength: number, variant: ListItemVariant): ComponentValidator => (
    () => (columnsLength !== columnWidthsLength ? `ListItem needs to have ${columnWidthsLength} columns for variant ${variant}.` : undefined)
);

export interface ListItemProps extends ListItemCanvasExternalProps {
    /**
     * Column components to fill in the ListItem
     */
    readonly columns: readonly JSX.Element[];

    /**
     * The column layout type.
     */
    readonly variant: ListItemVariant;

    /**
     * Optional flag to indicate that the list item's contents are disabled.
     */
    readonly disabled?: boolean;

    /**
     * Optional vertical alignment for ListItems, will align "center" by default
     */
    readonly verticalAlign?: Extract<StackProps["verticalAlign"], "start" | "center" | "stretch">;
}

interface ListItemInternalProps extends ListItemProps {
    readonly columnWidths: readonly number[];
}

// tslint:disable: no-magic-numbers
export const ListItem: React.FC<ListItemProps> = ({
    ...listItemProps
}) => (
    <ListItemInternal
        columnWidths={variantColumnWidths[listItemProps.variant]}
        {...listItemProps}
    />
);

const ListItemInternal: React.FC<ListItemInternalProps> = ({
    columns = [],
    columnWidths,
    disabled = false,
    menuDisabled = false,
    verticalAlign = "center",
    variant,
    cellRole,
    customListItemPadding,
    ...otherProps
}) => {
    validateProps("ListItemInternal", otherProps);

    //Verify column widths and number of columns match.
    useEffect(() => {
        validateComponent("ListItem", [buildListItemLengthValidator(columns.length, columnWidths.length, variant)]);
    }, [columns, columnWidths, variant]);

    //Ensures we only run the validation once at first, and then only for each
    //Column that changes after the first render
    useEffect(() => {
        columns.forEach((column) => (
            validateListItemColumnComponent(column, validColumnComponents)
        ));
    }, [columns]);

    const baseContent = (
        <SlotStack totalSlots={multiColumnTotalSlots} verticalAlign={verticalAlign} >
            {columns.map((item, index) => (
                <SlotStackItem slots={columnWidths[index]} key={item.key || index}>
                    {cellRole ?
                        (
                            <Stack role={cellRole}>
                                {
                                    React.cloneElement(item, { disabled: disabled || item.props.disabled, key: item.key || index })
                                }
                            </Stack>
                        ) :
                        React.cloneElement(item, { disabled: disabled || item.props.disabled, key: item.key || index })
                    }
                </SlotStackItem>
            ))}
        </SlotStack>
    );

    return (
        <ListItemCanvas {...otherProps} showDivider={otherProps.showDivider} padding={customListItemPadding ?? listItemPadding} disabled={disabled}>
            {baseContent}
        </ListItemCanvas>
    );
};
